import { useEffect, useState } from 'react'

import { createEnrollmentCheckpointBatcher } from './enrollment-checkpoints.js'

const pickFiniteNumber = value => (Number.isFinite(value) ? value : undefined)
const pickShortText = value =>
    typeof value === 'string' ? value.slice(0, 40) : undefined

// HiBu builds older than the KAL-5991 trace send no statusTrace; keep only known fields.
const pickStatusTrace = statusTrace => {
    if (!statusTrace || typeof statusTrace !== 'object') return null
    return {
        deviceCheckSource: pickShortText(statusTrace.deviceCheckSource),
        deviceCheckCooldownMs: pickFiniteNumber(
            statusTrace.deviceCheckCooldownMs
        ),
        deviceCheckFailureCount: pickFiniteNumber(
            statusTrace.deviceCheckFailureCount
        ),
        deviceCheckOutcome: pickShortText(statusTrace.deviceCheckOutcome),
        deviceCheckHttpStatus: pickFiniteNumber(
            statusTrace.deviceCheckHttpStatus
        ),
        deviceCheckMs: pickFiniteNumber(statusTrace.deviceCheckMs),
        enrollmentFetchState: pickShortText(statusTrace.enrollmentFetchState),
        enrollmentFetchMs: pickFiniteNumber(statusTrace.enrollmentFetchMs),
        headerRefreshOutcome: pickShortText(statusTrace.headerRefreshOutcome),
        headerRefreshMs: pickFiniteNumber(statusTrace.headerRefreshMs),
        totalMs: pickFiniteNumber(statusTrace.totalMs),
    }
}

const EMPTY_TRACE = {
    statusRequestsBeforeReply: 0,
    firstReplyAfterMs: null,
    firstReplyAction: null,
    extensionStatusTrace: null,
    extensionBridgeError: null,
}

const isStatusRequest = data =>
    data.type === 'COMMUNICATE_TO_HIBU' &&
    data.action === 'SEND_ENROLLMENT_STATUS'

const isBridgeError = data =>
    data.type === 'FROM_EXTENSION_DIAGNOSTIC' &&
    typeof data.bridgeError === 'string'

const isCheckpoint = data =>
    data.type === 'FROM_EXTENSION_DIAGNOSTIC' && data.checkpoint

const isWindowMessage = event =>
    event.source === window && event.data && typeof event.data === 'object'

const countRequest = current => ({
    ...current,
    statusRequestsBeforeReply: current.statusRequestsBeforeReply + 1,
})

const recordReply =
    ({ data, isFirstReply, firstRequestAt }) =>
    current => {
        const update = { ...current }
        if (isFirstReply) {
            // STATUS_UPDATE is HiBu's unprompted storage push, not an answer.
            update.firstReplyAction = pickShortText(data.action) ?? null
            update.firstReplyAfterMs =
                firstRequestAt === null ? null : Date.now() - firstRequestAt
        }
        if (data.statusTrace !== undefined) {
            update.extensionStatusTrace = pickStatusTrace(data.statusTrace)
        }
        return update
    }

// Read-only observer of the page <-> HiBu status messages for KAL-5991 diagnostics.
// Kept out of useBrowserEnrollment so its state never re-renders the other consumers.
const useEnrollmentStatusTrace = checkpointSender => {
    const [trace, setTrace] = useState(EMPTY_TRACE)

    useEffect(() => {
        let firstRequestAt = null
        let hasReplied = false
        const checkpoints = createEnrollmentCheckpointBatcher(batch =>
            checkpointSender.current?.(batch)
        )

        const onStatusRequest = () => {
            if (hasReplied) return
            if (firstRequestAt === null) firstRequestAt = Date.now()
            setTrace(countRequest)
        }

        const onBridgeError = data => {
            const extensionBridgeError = data.bridgeError.slice(0, 200)
            setTrace(current => ({ ...current, extensionBridgeError }))
        }

        const onExtensionReply = data => {
            const isFirstReply = !hasReplied
            hasReplied = true
            if (!isFirstReply && data.statusTrace === undefined) return
            setTrace(recordReply({ data, isFirstReply, firstRequestAt }))
        }

        const handleMessage = event => {
            if (!isWindowMessage(event)) return
            const { data } = event
            if (isCheckpoint(data)) {
                checkpoints.add(data.checkpoint)
                return
            }
            if (isStatusRequest(data)) onStatusRequest()
            else if (isBridgeError(data)) onBridgeError(data)
            else if (data.type === 'FROM_EXTENSION') onExtensionReply(data)
        }

        window.addEventListener('message', handleMessage)
        return () => {
            window.removeEventListener('message', handleMessage)
            checkpoints.dispose()
        }
    }, [checkpointSender])

    return trace
}

export default useEnrollmentStatusTrace
